<template>
  <div class="modal fade show" tabindex="-1" style="display: block; background: rgba(0,0,0,0.5);" v-if="isOpen">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEdit ? 'Cập nhật' : 'Thêm mới' }} ngân hàng</h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="save">
            <div class="row">
              <div class="col-md-12 mb-3">
                <label class="form-label">Chọn ngân hàng từ VietQR</label>
                <select class="form-select" v-model="selectedVietQrBank" @change="onVietQrBankChange">
                  <option value="">-- Chọn ngân hàng --</option>
                  <option v-for="b in vietQrBanks" :key="b.bin" :value="JSON.stringify(b)">
                    {{ b.shortName }} - {{ b.name }}
                  </option>
                </select>
                <small class="text-muted">Chọn để tự động điền thông tin bên dưới</small>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Tên ngân hàng *</label>
                <input type="text" class="form-control bg-light" v-model="form.bank_name" required readonly>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Tên ngắn gọn</label>
                <input type="text" class="form-control bg-light" v-model="form.short_name" readonly>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Mã (acq_id/BIN) *</label>
                <input type="text" class="form-control bg-light" v-model="form.acq_id" required readonly>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Logo URL</label>
                <input type="text" class="form-control bg-light" v-model="form.logo_url" readonly>
                <div class="mt-2 border rounded p-2 bg-white d-inline-block" v-if="form.logo_url">
                  <img :src="form.logo_url" alt="logo" style="height: 50px; max-width: 200px; object-fit: contain;" />
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Số tài khoản *</label>
                <input type="text" class="form-control" v-model="form.account_number" required>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Tên chủ tài khoản *</label>
                <input type="text" class="form-control" v-model="form.account_name" required>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Thứ tự hiển thị</label>
                <input type="number" class="form-control" v-model="form.sort_order">
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Trạng thái</label>
                <div class="form-check form-switch mt-2">
                  <input class="form-check-input" type="checkbox" v-model="form.is_active" id="isActiveSwitch">
                  <label class="form-check-label" for="isActiveSwitch">{{ form.is_active ? 'Hoạt động' : 'Tạm ẩn' }}</label>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Tài khoản công ty</label>
                <div class="form-check form-switch mt-2">
                  <input class="form-check-input" type="checkbox" v-model="form.is_company" id="isCompanySwitch">
                  <label class="form-check-label" for="isCompanySwitch">
                    <span v-if="form.is_company" class="text-warning fw-semibold">Tài khoản công ty</span>
                    <span v-else class="text-muted">Tài khoản thông thường</span>
                  </label>
                </div>
                <small class="text-muted" v-if="form.is_company">Chỉ các khách hàng được gán mới thấy và nạp tiền vào tài khoản này</small>
              </div>
              <div class="col-md-12 mb-3">
                <label class="form-label">Mô tả thêm</label>
                <textarea class="form-control" v-model="form.description" rows="2"></textarea>
              </div>
            </div>
            <div class="text-end mt-3">
              <button type="button" class="btn btn-secondary me-2" @click="close">Hủy</button>
              <button type="submit" class="btn btn-primary" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                Lưu lại
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, watch} from 'vue'
import axios from 'axios'
import { useBankAccounts } from '@/composables/useBankAccounts'

const emit = defineEmits(['saved'])
const { createBankAccountApi, updateBankAccountApi } = useBankAccounts()

const isOpen = ref(false)
const isEdit = ref(false)
const loading = ref(false)
const editId = ref(null)

const vietQrBanks = ref([])
const selectedVietQrBank = ref("")

const defaultForm = {
  bank_name: '',
  short_name: '',
  acq_id: '',
  logo_url: '',
  account_number: '',
  account_name: '',
  is_active: true,
  is_company: false,
  description: '',
  sort_order: 0
}

const form = ref({ ...defaultForm })

const fetchVietQrBanks = async () => {
  try {
    const res = await axios.get('https://api.vietqr.io/v2/banks')
    if (res.data && res.data.code === '00') {
      vietQrBanks.value = res.data.data
    }
  } catch (error) {
    console.error('Lỗi khi lấy danh sách VietQR bank', error)
  }
}

const isSafeUrl = (url) => {
  try {
    const parsed = new URL(url)
    return ['http:', 'https:'].includes(parsed.protocol)
  } catch {
    return false
  }
}

const onVietQrBankChange = () => {
  if (selectedVietQrBank.value) {
    const bank = JSON.parse(selectedVietQrBank.value)
    form.value.bank_name = bank.name
    form.value.short_name = bank.shortName
    form.value.acq_id = bank.bin
    form.value.logo_url = isSafeUrl(bank.logo) ? bank.logo : ''
  }
}

// Watch for banks list to auto-select if open() was called before banks were loaded
watch(vietQrBanks, (newBanks) => {
  if (isEdit.value && form.value.acq_id && !selectedVietQrBank.value && newBanks.length > 0) {
    const match = newBanks.find(b => b.bin === form.value.acq_id)
    if (match) {
      selectedVietQrBank.value = JSON.stringify(match)
    }
  }
})

const open = (data = null) => {
  if (data) {
    isEdit.value = true
    editId.value = data.id
    form.value = { ...data, is_active: Boolean(data.is_active), is_company: Boolean(data.is_company) }

    // Auto-select the bank in the VietQR dropdown if it exists
    if (vietQrBanks.value.length > 0 && data.acq_id) {
      const match = vietQrBanks.value.find(b => b.bin === data.acq_id)
      if (match) {
        selectedVietQrBank.value = JSON.stringify(match)
      }
    }
  } else {
    isEdit.value = false
    editId.value = null
    form.value = { ...defaultForm }
    selectedVietQrBank.value = ""
  }
  isOpen.value = true
}

const close = () => {
  isOpen.value = false
}

const save = async () => {
  loading.value = true
  try {
    const payload = { ...form.value }
    let res
    if (isEdit.value) {
      res = await updateBankAccountApi(editId.value, payload)
    } else {
      res = await createBankAccountApi(payload)
    }

    if (res.success) {
      close()
      emit('saved')
    } else {
      alert(res.error)
    }
  } catch (error) {
    alert('Có lỗi xảy ra')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchVietQrBanks()
})

defineExpose({ open, close })
</script>
